import React, { useState, useEffect, useContext, createContext } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, setLogLevel } from 'firebase/firestore';
import { unsubscribeUserToPush } from '../notify/subscribe.js';
import { firebaseConfig as importedFirebaseConfig } from "../firebase-config.js";
import { config } from '../config';


const canvasFirebaseConfig = typeof window.__firebase_config !== 'undefined'
    ? JSON.parse(window.__firebase_config)
    : {};

const effectiveFirebaseConfig = Object.keys(canvasFirebaseConfig).length > 0 ? canvasFirebaseConfig : importedFirebaseConfig;


const initialAuthToken = typeof window.__initial_auth_token !== 'undefined'
    ? window.__initial_auth_token
    : null;


const AuthContext = createContext(null);

/**
 * Helper to sign the user into the Firebase Client SDK using the Custom Token.
 */
async function validateToken(authInstance, customToken) {
    if (!authInstance || !customToken) return false;
    try {
        // console.log("Attempting Firebase client sign-in with Custom Token...");
        await signInWithCustomToken(authInstance, customToken);
        return true;
    } catch (error) {
        // console.error("Firebase Custom Token sign-in failed:", error);
        throw new Error(error.message || "Token verification failed during Firebase sign-in.");
    }
}

/**
 * The core logic hook that manages Firebase state and operations.
 */
const useProvideAuth = () => {
    const [auth, setAuth] = useState(null);
    const [db, setDb] = useState(null);
    const [userId, setUserId] = useState(null);
    const [username, setUsername] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // Initialize Firebase and set up Auth Listener
    useEffect(() => {
        setLogLevel('debug'); // Enable detailed Firebase logging

        if (!effectiveFirebaseConfig || !effectiveFirebaseConfig.apiKey) {
            // console.error("CRITICAL: Final determined Firebase configuration is missing or invalid. Initialization halted.");
            setIsAuthReady(true);
            return;
        }

        // console.log("Firebase initialized with Project ID:", effectiveFirebaseConfig.projectId);

        try {
            //Initialize Firebase App and Service
            const app = initializeApp(effectiveFirebaseConfig);
            const authInstance = getAuth(app);
            setAuth(authInstance);
            setDb(getFirestore(app));

            //Initial Authentication (with custom token if available)
            const authenticate = async () => {
                try {
                    if (initialAuthToken) {
                        // console.log("Attempting initial sign-in via environment token.");
                        await signInWithCustomToken(authInstance, initialAuthToken);
                    }
                } catch (error) {
                    // console.error("Firebase Initial Auth Error (Environment Token):", error);
                }
            };

            //Auth State Listener
            const unsubscribe = onAuthStateChanged(authInstance, (user) => {
                if (user) {
                    setUserId(user.uid);
                    setIsLoggedIn(true);
                    // Restore username from localStorage if available
                    const storedUsername = localStorage.getItem('username');
                    if (storedUsername) {
                        setUsername(storedUsername);
                    }
                    // console.log("User signed in with UID:", user.uid);
                } else {
                    setUserId(null);
                    setIsLoggedIn(false);
                    setUsername(null);
                    // console.log("User signed out or failed sign-in.");
                }
                setIsAuthReady(true);
            });

            authenticate();
            return () => unsubscribe();

        } catch (e) {
            // console.error("Failed to initialize Firebase:", e);
        }
    }, []);

    // ...

    // Function to handle login via FastAPI and Custom Token
    const handleLogin = async (usernameInput, password) => {
        if (!auth) {
            throw new Error("Firebase Auth not initialized.");
        }
        const API_URL = `${config.API_BASE_URL}/api/login7`;

        try {
            //Call FastAPI to Validate Credentials and Get Custom Token
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: usernameInput, password }),
            });

            if (!response.ok) {
                // Attempt to parse JSON error detail from backend
                const data = await response.json().catch(() => ({ detail: `HTTP Error: ${response.status}` }));
                let errorMessage = data.detail || `Authentication failed with status: ${response.status}`;
                if (typeof errorMessage === 'object') {
                    errorMessage = JSON.stringify(errorMessage);
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            const customToken = data.token;

            if (!customToken) {
                throw new Error("Backend did not return a Firebase custom token.");
            }

            //Log into Firebase client SDK
            await validateToken(auth, customToken);

            //Save username to state and localStorage
            setUsername(usernameInput);
            localStorage.setItem('username', usernameInput);

            return true;

        } catch (e) {
            // console.error("Login Process Failed:", e);
            throw e; // Re-throw the error for the component to handle
        }
    };

    // Standard Firebase Logout
    const handleLogout = async () => {
        if (auth) {
            try {
                // Unsubscribe using the stored username
                // Only unsubscribe if the user has notifications enabled in the app
                const isPushEnabled = localStorage.getItem('pushEnabled') === 'true';

                if (username && isPushEnabled) {
                    const registration = await navigator.serviceWorker.register('/service-worker.js');
                    const subscription = await registration.pushManager.getSubscription();
                    if (subscription) {
                        await unsubscribeUserToPush(registration, username);
                    }
                }

                await signOut(auth);

                // Clear local storage
                localStorage.removeItem('username');
                setUsername(null);

            } catch (e) {
                // console.error("Logout failed:", e);
            }
        }
    };

    // Return the state and functions to be provided to the rest of the app
    return { userId, isLoggedIn, isAuthReady, handleLogin, handleLogout, db, username };
};

// Component to wrap the application and provide auth state
export const AuthProvider = ({ children }) => {
    const auth = useProvideAuth();
    return (
        <AuthContext.Provider value={auth}>
            {/* Show a loading state until Firebase Auth listener is ready */}
            {auth.isAuthReady ? children :
                <div style={{ minHeight: '100vh' }} className="flex justify-center items-center text-indigo-600">
                    Initializing Authentication...
                </div>
            }
        </AuthContext.Provider>
    );
};

// Hook for consuming the auth context in any component
export const useAuth = () => {
    return useContext(AuthContext);
};